import { useCallback, useRef } from "react";
import type { EventPayload } from "@/common/types/chat.types";

export interface EventListener<T extends EventPayload = EventPayload> {
  (payload: T): void;
}

export interface InternalEventEmitter {
  on<T extends EventPayload = EventPayload>(event: string, listener: EventListener<T>): void;
  off<T extends EventPayload = EventPayload>(event: string, listener: EventListener<T>): void;
  emit<T extends EventPayload = EventPayload>(event: string, payload: T): T;
}

/**
 * Hook that provides an internal event emitter for component communication.
 * Supports InterceptableEvent that you can check and stop further processing in chain or caller.
 */
export function useInternalEventEmitter(): InternalEventEmitter {
  const listenersRef = useRef<Map<string, Set<EventListener>>>(new Map());

  const on = useCallback(<T extends EventPayload = EventPayload>(
    event: string, 
    listener: EventListener<T>
  ): void => {
    let listeners = listenersRef.current.get(event);
    if (!listeners) {
      listeners = new Set<EventListener>();
      listenersRef.current.set(event, listeners);
    }
    listeners.add(listener as EventListener);
  }, []);

  const off = useCallback(<T extends EventPayload = EventPayload>(
    event: string,
    listener: EventListener<T>
  ): void => {
    const listeners = listenersRef.current.get(event);
    if (listeners?.delete(listener as EventListener) && listeners.size === 0) {
      listenersRef.current.delete(event);
    }
  }, []);

  const emit = useCallback(<T extends EventPayload = EventPayload>(
    event: string, 
    payload: T
  ): T => {
    const listeners = listenersRef.current.get(event);
    
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(payload);
        } catch (error) {
          console.error(`InternalEventEmitter: Error in listener for event "${event}":`, error);
        }
      });
    }

    return payload;
  }, []);

  return { on, off, emit };
}